<template>
  <div>
    <PlatformMobileTopBar
      v-if="showTopBar"
      class="mobile-top-bar-container"
      :primary-color="primaryColor"
      :profile-details="mobileUserProfile"
      :notifications-badge="notificationsBadgeValue"
      :show-ai-chat="showAiChat"
      @profile-click="onMobileProfileClick"
      @search-click="onMobileSearchClick"
      @notifications-click="onMobileNotificationsClick"
      @ai-chat-click="openPanel" />

    <PlatformProfilePanel
      :is-open="isMobileProfileOpen"
      :user-name="mobileUserProfile.displayName"
      :email="mobileUserProfile.email"
      :color-id="mobileUserProfile.colorId"
      :image-path="mobileUserProfile.imagePath"
      :account-items="mobileAccountItems"
      :logout-label="$t('common.navigation.logout')"
      @close="onMobileProfileClose"
      @item-click="onProfileItemClick"
      @logout="onMobileLogout" />

    <PlatformBottomBar v-if="shouldShowBottomBar" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import PlatformBottomBar from './PlatformBottomBar.vue';
import PlatformMobileTopBar from './PlatformMobileTopBar.vue';
import PlatformProfilePanel from './PlatformProfilePanel.vue';
import {
  buildPlatformMobileProfilePanelItems,
  getUserProfileDetails,
} from '../../utils/accountMenuUtils';
import MOBILE_PLATFORM_ROUTE_NAMES from '../../utils/mobilePlatformRoutes';

const MOBILE_GLASSY_NAV_FLAG = 'rollout.platform_administration.mobile_glassy_nav_layout';
const MOBILE_PLATFORM_NAV_FLAG = 'rollout.platform_administration.mobile_platform_nav';

export default {
  name: 'MobileGlassyNavigation',
  components: {
    PlatformBottomBar,
    PlatformMobileTopBar,
    PlatformProfilePanel,
  },
  data() {
    return {
      isMobileProfileOpen: false,
      isMobilePlatformNavigationReady: false,
    };
  },
  async created() {
    this.initNotificationCenterBadge({ staffUid: this.staffUid });
    await this.initMobilePlatformNavigation();
  },
  computed: {
    ...mapGetters('MainMenuStore', ['shouldShowMenu']),
    ...mapGetters('NotificationCenterStore', { notificationCount: 'isNewCounter' }),
    ...mapGetters('BusinessStore', ['hasFeature', 'primaryColor', 'attributes']),
    ...mapGetters('StaffStore', ['staffUid', 'staffData', 'hasPermission']),
    ...mapGetters('AngularStore', ['isAngularLoaded']),
    ...mapGetters('PlatformNavigationStore', [
      'hasFatalError',
      'navigationTree',
      'showSidebar',
    ]),
    isMobilePlatformNavigationActive() {
      return this.hasFeature(MOBILE_GLASSY_NAV_FLAG) && this.hasFeature(MOBILE_PLATFORM_NAV_FLAG);
    },
    isDashboardRoute() {
      return this.$route?.name === 'dashboard';
    },
    isMobilePlatformHomeRoute() {
      return this.$route?.name === MOBILE_PLATFORM_ROUTE_NAMES.HOME;
    },
    showTopBar() {
      return this.shouldShowMenu || this.isDashboardRoute || this.isMobilePlatformHomeRoute;
    },
    shouldShowBottomBar() {
      return !this.isMobilePlatformNavigationActive || this.isMobilePlatformNavigationReady;
    },
    showAiChat() {
      return this.$hasFeature('pkg.ai.bizai_chat') || this.$hasFeature('pkg.ai.bizai20');
    },
    notificationsBadgeValue() {
      if (!this.notificationCount) return 0;
      return this.notificationCount > 99 ? '+99' : this.notificationCount;
    },
    mobileUserProfile() {
      return getUserProfileDetails(this.staffData, this.attributes);
    },
    mobileAccountItems() {
      return buildPlatformMobileProfilePanelItems({
        t: (key) => this.$t(key),
        hasFeature: (key) => this.$hasFeature(key),
        hasPermission: this.hasPermission,
        staffRole: this.staffData?.role,
      });
    },
  },
  methods: {
    ...mapActions('AiChatStore', { openAiChatPanel: 'openPanel' }),
    openPanel() { this.openAiChatPanel('top_bar'); },
    ...mapActions('MainMenuStore', ['openSearchAction', 'updateShouldShowMenu']),
    ...mapActions('NotificationCenterStore', ['initNotificationCenterBadge']),
    ...mapActions('AuthStore', { mobileLogout: 'onLogout' }),
    ...mapActions('PlatformNavigationStore', ['fetchNavigation', 'setActiveItemFromRoute', 'clearActiveTopBarItem']),
    async initMobilePlatformNavigation() {
      if (!this.isMobilePlatformNavigationActive) {
        this.isMobilePlatformNavigationReady = true;
        return;
      }

      try {
        await this.fetchNavigation();
        this.syncActiveItemFromRoute(this.$route);
      } catch {
        this.$router.push('/error');
      } finally {
        this.isMobilePlatformNavigationReady = true;
      }
    },
    syncActiveItemFromRoute(route) {
      if (route?.meta?.preserveMobilePlatformNavigation) return;
      if (route?.name === MOBILE_PLATFORM_ROUTE_NAMES.HOME) {
        this.clearActiveTopBarItem();
        return;
      }

      this.setActiveItemFromRoute({
        path: route.path,
        showModuleGridOnNoMatch: true,
      });
    },
    replaySidebarState() {
      outgoingPostMessageUtils.post('toggle-sidebar-visibility', {
        showSidebar: this.showSidebar,
      });
    },
    onMobileProfileClick() {
      this.$track('mobile_profile_opened');
      this.isMobileProfileOpen = true;
    },
    onProfileItemClick(item) {
      this.updateShouldShowMenu(false);
      this.isMobileProfileOpen = false;
      if (item.routingType === 'pov' && item.route) {
        this.$router.push({ name: item.route, params: item.routeData || {} });
      } else if (item.route) {
        outgoingPostMessageUtils.post('navigate-to', { route: item.route, data: item.routeData });
      }
    },
    onMobileProfileClose() {
      this.isMobileProfileOpen = false;
    },
    onMobileLogout() {
      this.updateShouldShowMenu(false);
      this.isMobileProfileOpen = false;
      this.mobileLogout();
    },
    onMobileSearchClick() {
      this.openSearchAction();
    },
    onMobileNotificationsClick() {
      outgoingPostMessageUtils.post('navigate-to', { route: 'app.notifications' });
    },
  },
  watch: {
    $route(to) {
      if (this.isMobilePlatformNavigationActive) {
        this.syncActiveItemFromRoute(to);
      }
    },
    navigationTree(tree) {
      if (this.isMobilePlatformNavigationActive && tree?.length) {
        this.syncActiveItemFromRoute(this.$route);
      }
    },
    hasFatalError(hasError) {
      if (hasError) {
        this.$router.push('/error');
      }
    },
    isAngularLoaded(loaded) {
      if (loaded && this.isMobilePlatformNavigationActive) {
        this.replaySidebarState();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.mobile-top-bar-container {
  width: 100%;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  pointer-events: all;
}
</style>
